/**
 * One autosaved cell = one PATCH carrying the row's version (f11.1).
 *
 * On success the returned line replaces the row in the grid cache and the
 * grid is marked pending, which starts the polling. On
 * `409 FORECAST_LINE_VERSION_CONFLICT` the row is replaced by the server's
 * `current` line and the caller receives the change back so it can offer
 * "Reapply my change" against the new version (adr-14).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type { ForecastGrid, ForecastLine, ForecastLineChange } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';

export const VERSION_CONFLICT = 'FORECAST_LINE_VERSION_CONFLICT';
export const MONTH_CLOSED = 'MONTH_CLOSED';
export const CYCLE_NOT_OPEN_FOR_INPUT = 'CYCLE_NOT_OPEN_FOR_INPUT';

const TIME_WINDOW_REASONS: ReadonlySet<string> = new Set([MONTH_CLOSED, CYCLE_NOT_OPEN_FOR_INPUT]);

/**
 * A hard time-window denial from the server (f11.5): a stale tab after the
 * closing month moved or the cycle left input. The grid is refetched and the
 * local edit dropped -- there is nothing to reapply.
 */
export function isTimeWindowRefusal(error: unknown): error is ApiError {
  return error instanceof ApiError && error.reason != null && TIME_WINDOW_REASONS.has(error.reason);
}

/** A change without its version: the version is always the row's current one. */
export type CellChange = Omit<ForecastLineChange, 'expected_version'>;

export interface CellSave {
  line: ForecastLine;
  change: CellChange;
}

function isLine(value: unknown): value is ForecastLine {
  return typeof value === 'object' && value !== null && 'id' in value && 'version' in value;
}

/** The current line a version conflict carries, if it carries one. */
export function conflictLine(error: unknown): ForecastLine | null {
  if (!(error instanceof ApiError) || error.reason !== VERSION_CONFLICT) return null;
  const body = error.body as { current?: unknown } | null;
  return isLine(body?.current) ? body.current : null;
}

function replaceLine(grid: ForecastGrid | undefined, line: ForecastLine, pending: boolean): ForecastGrid | undefined {
  if (!grid) return grid;
  return {
    ...grid,
    lines: grid.lines.map((existing) => (existing.id === line.id ? line : existing)),
    recalculation: pending ? { ...grid.recalculation, pending: true } : grid.recalculation,
  };
}

export function useUpdateForecastLine(cycleId: string, businessUnitId: string) {
  const client = useQueryClient();
  const key = queryKeys.cycle.forecastGrid(cycleId, businessUnitId);

  return useMutation({
    mutationFn: ({ line, change }: CellSave) =>
      api.updateForecastLine(cycleId, line.id, { ...change, expected_version: line.version }),
    onSuccess: (result) => {
      client.setQueryData<ForecastGrid>(key, (grid) => replaceLine(grid, result.line, true));
    },
    onError: (error) => {
      const current = conflictLine(error);
      if (current) {
        client.setQueryData<ForecastGrid>(key, (grid) => replaceLine(grid, current, false));
      } else if (isTimeWindowRefusal(error)) {
        void client.invalidateQueries({ queryKey: key });
      }
    },
  });
}
